<template>
  <div class="horizontal-filter-container" :class="{ points: page == 'points'}">
    <div class="spacer" v-if="page == 'map'"></div>
     
      <p class="message" v-show="!(showResetButton && isFilterEnabled)">
        <span class="material-symbols-sharp" data-icon="info"></span>
        <span>こちらから目的の実績を絞り込めます</span>
      </p>

      <div class="horizontal-filter" :class="{ center: !isScroll }">        
        <div class="scroll-icon left" v-if="isScroll" @mousedown="onClickScrollLeft">
          <span class="material-symbols-sharp" data-icon="chevron_left"></span>
        </div>

        <div class="dropdown-item" v-for="(parent) in filter">
          <template v-if="!isInternal && !parent?.extra_fields?.open_children">
            <button class="select-menu uk-button uk-button-small" :class="{ selected: isSelectedChildren(parent) }"
              type="button" @click.stop="onFilterClicked(parent)" :data-id="parent.id">
              <span class="material-symbols-sharp filled check-icon" data-icon="check_circle"></span>
              <span class="label">{{ parent.name }}</span>
            </button>
          </template>

          <template v-else>
            <button class="select-menu uk-button uk-button-small" :class="{ selected: isSelectedChildren(parent) }"
              type="button" :data-id="parent.id">
              <span class="material-symbols-sharp filled check-icon" data-icon="check_circle"
                @click.stop="onFilterClicked(parent)"></span>
              <span class="label">{{ parent.name }}</span>
              <span class="material-symbols-sharp filled check-icon arrow_drop_down" data-icon="arrow_drop_down"></span>
            </button>

            <div class="uk-dropdown-large uk-padding-remove"
              uk-dropdown="pos:bottom-left; mode: click; flip: false; close-on-scroll: true">
              <ul class="uk-nav uk-dropdown-nav uk-button uk-button-default search-condition-selector">
                <li class="search-condition-item" :class="{ selected: isSelected(filter.id) }"
                  @click="onFilterClicked(filter)" v-for="filter in parent.children">
                  <span class="material-symbols-sharp filled check-icon" data-icon="check_circle"></span>
                  <span class="label">{{ filter.name }}</span>
                </li>
              </ul>
            </div>
          </template>


        </div>

        <div class="scroll-icon right" v-if="isScroll" @click="onClickScrollRight()">
          <span class="material-symbols-sharp" data-icon="chevron_right"></span>
        </div>

      </div>


    <div class="reset-container" v-show="showResetButton && isFilterEnabled">
      <button class="reset-filter uk-button uk-button-small"
        @click="onResetFilterClicked" :disabled="!isFilterEnabled">
        <span uk-icon="icon: close; ratio: 0.7"></span>
        <span>{{ messages.map_reset_filters }}</span>
      </button>
      
      <div class="reset-message">
        <span class="material-symbols-sharp" data-icon="info"></span>
        <span>こちらのボタンから絞り込みを解除できます</span>
      </div>

    </div>

    
  </div>
</template>


<script setup lang="ts">
/**
 * 日軽エンジニアリング様専用の横並び絞り込みUIコンポーネント
 * FIlter.vueコンポーネントをベースに、日軽さんの利用方法に合わせて必要最低限残したもの
 **
 * MEMO
 * 汎用化する場合は色々検討が必要
 * 特にドロップダウンメニューは、商品検索系などで条件が多い場合は特に相性が悪い。
 */

import { computed, ref, onMounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import { Filters } from "@/services/Filters.ts"
import PointsService from "@/services/PointsService.ts"
import {_debounce} from "@/core/common.js"

const props = defineProps({
  /**
   * Filters.TYPE_...
   */
  type: {
    type: String,
    default: Filters.TYPE_SEARCH_CONDITIONS
  },
  /**
   * Filter
   */
  filter: {
    type: Object,
    default: null
  },

  /**
   * 社内向けマップだと true
   * 社外マップでは子要素が選択できない
   */
  isInternal: {
    type: Boolean,
    default: false
  },

  /**
   * 設置するページ
   * レイアウトの調整用
   */
  page: {
    type: String,
    default: "map"
  },

  /**
   * 絞り込みリセットの表示
   * マップページは地図上にあるので主に一覧ページ用
   */
  showResetButton: {
    type: Boolean,
    default: false
  }

})

const store = useStore()

// 管理画面で設定されたメッセージの参照用（現状未使用）
const messages = computed(() => storelocator.messages)

/**
 * 絞り込み状態の確認
 * このコンポーネントでは絞り込み条件だけ解除したいが、areaFilterが有効だとそちらもみてしまうので、selectedSearchConditionsの数を確認する
 * 本来であればURL絞り込みの初期状態と比較する必要があるが、日軽はそのユースケースは無いのでlengthが0なら絞り込んでいないと判断
 */
const isFilterEnabled = computed(() => {
  return store.state.filters.selectedSearchConditions.length != 0
})


/**
 * 選択状態の判定
 */
const isSelected = computed(() => (id: String) => {
  return store.state.filters["selectedSearchConditions"].includes(id)
})

/**
 * true: 子を選択中
 */
interface Filter {
  id: string;
  name: string;
  children: { id: string; name: string }[];
}

const isSelectedChildren = computed(() => (filter: Filter) => {
  return filter.children.some((child) => store.state.filters["selectedSearchConditions"].includes(child.id))
})


/**
 * 絞り込み表示エリアの横スクロールの監視
 * ウィンドウサイズによって表示が切り替わる要素（左右の<>）があるのと、レイアウト調整を行ってる
 */
const isScroll = ref(false);


/**
 * ウィンドウサイズ変更時の処理
 * overflow-containerのスクロールの有無を確認して、isScrollの値を更新
 */
const handleWindowSizeChange = () => {
  const targetDOM = document.querySelector('.horizontal-filter')
  if (targetDOM.scrollWidth > targetDOM.clientWidth) {
    isScroll.value = true
    return
  }
  isScroll.value = false
}


// ウィンドウサイズ変更時の関数へでバウンスを仕込み
const debouncedScroll = _debounce(handleWindowSizeChange, 300)

// 監視イベントをセット
window.addEventListener("resize", debouncedScroll);


onMounted(() => {
  nextTick(async () => {
    // ウィンドウサイズの初回判定用
    // nextTick使ってもDOMの更新前に評価されてしまうので、ちょっとだけ遅延
    await new Promise(resolve => setTimeout(resolve, 300))
    handleWindowSizeChange()
  })
})

/**
 * 絞り込みを選択
 *
 * @param filter Filter
 * @param parentId 指定すると親の選択状態を更新する
 */
function onFilterClicked(filter: Filter, parentId: string = null) {
  const propName = props.type === Filters.TYPE_MARKERS
    ? "selectedMarkers"
    : "selectedSearchConditions"

  const isParent = parentId ? true : false
  const setId = parentId ? parentId : filter.id

  store.dispatch("changeFilter", {
    type: props.type,
    id: setId,
    parent: isParent,
    noApply: false
  })


  // 以下のケースで親を更新
  // ・親が選択中で、兄弟のいずれかが未選択
  // ・親が未選択で、兄弟が全選択

  const parent = Filters.findParentById(props.type, setId)
  if (!parent) {
    return
  }

  const siblingIds = parent.children.map(p => p.id)
  const checkSiblingIdsSelect = siblingIds.map(sid => {
    return store.state.filters[propName].includes(sid)
  })

  if ((store.state.filters[propName].includes(parent.id) && checkSiblingIdsSelect.includes(false))
    || (!store.state.filters[propName].includes(parent.id) && !checkSiblingIdsSelect.includes(false))
  ) {
    onFilterClicked(filter, parent.id)
  }
}


function onResetFilterClicked() {
  PointsService.resetFilter()
}

/**
 * 左スクロールボタンを押下した時に隠れている直近のボタン位置まで移動する
 */
function onClickScrollLeft() {
  const container = document.querySelector(".horizontal-filter")
  const elements = document.querySelectorAll(".dropdown-item")
  const hideElements: Array<string> = []

  // 見切れているボタンを確認
  elements.forEach(element => {
    hideElements.push(checkHiddenSelect(element, container))
  })

  // なければ終了
  if (!hideElements.includes("left")) {
    return
  }

  // 表示できる位置までスクロール 「-48」は余白分
  const lastIndex = hideElements.lastIndexOf("left")

  const containerRect = container.getBoundingClientRect()
  const elementRect = elements.item(lastIndex).getBoundingClientRect()
  const targetPos = container.scrollLeft + elementRect.left - containerRect.left - 48

  container.scroll({
    top: 0,
    left: targetPos,
    behavior: "smooth",
  })
}

/**
 * 右スクロールボタンを押下した時に隠れている直近のボタン位置まで移動する
 */
function onClickScrollRight() {
  const container = document.querySelector(".horizontal-filter")
  const elements = document.querySelectorAll(".dropdown-item")
  const hideElements: Array<string> = []

  // 見切れているボタンを確認
  elements.forEach(element => {
    hideElements.push(checkHiddenSelect(element, container))
  })

  // なければ終了
  if (!hideElements.includes("right")) {
    return
  }

  // 表示できる位置までスクロール 「+48」は余白分
  const lastIndex = hideElements.indexOf("right")
  const containerRect = container.getBoundingClientRect()
  const elementRect = elements.item(lastIndex).getBoundingClientRect()
  const targetPos = container.scrollLeft + elementRect.right + 48 - containerRect.right

  container.scroll({
    top: 0,
    left: targetPos,
    behavior: "smooth",
  })
}

/**
 * スクロールするコンテナのエレメントに対し、絞り込みボタンが見切れているかを確認する
 *
 * @param element チェックしたい絞り込みボタンのエレメント
 * @param container スクロールするコンテナのエレメント
 *
 * @return 見切れていれば、見切れている方法を示すキーワードを返す left | null | right
 */
function checkHiddenSelect(element: Element, container: Element) {
  const elementRect = element.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()

  let isHidden = null

  if (elementRect.left - 48 < containerRect.left) {
    isHidden = "left"
  } else if (elementRect.right + 48 > containerRect.right) {
    isHidden = "right"
  }

  return isHidden
}

</script>

<style lang="scss" scoped>
$horizontal-filter-height: 80px;

.horizontal-filter-container {
  display: flex;
  position: absolute;
  z-index: 1;
  width: 100%;
  background-color: #fff;

  .spacer {
    background-color: #fff;
    width: 0;
    flex-shrink: 0;
    flex-grow: 0;

    background-color: #fff;
    width: 0;
    flex-shrink: 0;
    flex-grow: 0;

    @media only screen and (min-width: $breakpoint-small) {
      width: 0;
    }

    @media only screen and (min-width: #{$breakpoint-medium}) {
      width: 340px;
    }
  }

  .horizontal-filter {
    display: flex;
    overflow: auto;
    gap: 1rem;
    align-items: center;
    width: 100%;
    height: $horizontal-filter-height;
    
    &.center {
      justify-content: center;
    }

    &::-webkit-scrollbar {
      height: 5px;
    }

    &::-webkit-scrollbar-track {
      background-color: #f4f4f4;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #aaa;
    }


  }
}

.scroll-icon {
  position: sticky;

  display: flex;
  align-items: center;
  background-color: #fffe;
  height: 100%;
  z-index: 100;
  padding: 0 0.5rem;
  color: #ccc;
  cursor: pointer;

  &.left {
    left: 0;
    border-right: solid 1px #f3f3f3;

  }
  &.right {
    right: 0;
    border-left: solid 1px #f3f3f3;
  }
}

.dropdown-item {
  flex-shrink: 0;
  font-weight: bold;
  margin-top: 20px;

  .check-icon {
    font-size: 1.5em;
    color: #444;
    opacity: 0.3;
  }

  .select-menu {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding-left: 0.5rem;
    padding-right: 1rem;
    transition: all .25s;
    background-color: #f6f6f6;
    border-radius: 1px;
    box-shadow: 0 1px 3px 0px rgba(0, 0, 0, .3);
    &:hover {
      background-color: #ddd;
    }

    .label {
      transition: all .25s;
    }

    .arrow_drop_down {
      opacity: 0.6;
      margin-right: -0.5rem;
    }

    &.selected {
      background-color: var(--theme-color-tp05);

      .check-icon {
        color: var(--theme-color);
        opacity: 1;
      }

      .label {
        transform: translateX(5px);
      }
    }
  }
}


.search-condition-item {
  text-align: left;
  padding-left: 0.5em;
  padding-right: 1.5em;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: .5rem;
  transition: all .25s;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background-color: #ddd;
  }

  .label {
    transition: all .25s;
  }

  &.selected {
    background-color: var(--theme-color-tp05);
    border-radius: 3px;

    .check-icon {
      color: var(--theme-color);
      opacity: 1;
    }

    .label {
      transform: translateX(5px);
    }
  }
}

.search-condition-selector {
  padding: 0.5rem;
  background-color: #f2f2f2;
  max-height: 60dvh;
  overflow: auto;
  }

.uk-dropdown {
  box-shadow: 0 5px 12px rgba(0, 0, 0, .15);
}

// 一覧ページ用
.horizontal-filter-container.points {
  position: relative;
  padding: 0;
  display: block;
  width: 100%;
  min-width: 0;

  .reset-container {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-top: 0.5rem;
  }

  .reset-filter {
    font-size: .75rem;
    border: solid 1px #ddd;
    border-radius: 99em;
    padding: 0rem .5rem;    
    color: #444;
    background-color: #fff;
    transition: all .25s;
    display: flex;
    align-items: center;
    gap: .25rem;
    line-height: 24px;
    flex-shrink: 0;
    &:hover {
      color: #fff;
      background-color: var(--theme-color);
      border-color: var(--theme-color);
    }
  }

  .search-condition-selector {
    max-height: 45dvh;    
  }
}

.message {
  margin: 2px 0 0 0;
  padding: 4px;
  position: absolute;
  top: 0px;
  left: 3.25rem;
  text-align: center;
  font-size: .875rem;
  display: flex;
  align-items: center;
  gap: 4px;
  color: #5fa8da;

  .material-symbols-sharp {
    font-size: .875rem;    
  }

  @media only screen and (min-width: $breakpoint-medium) {
    left: calc(340px + 3.25rem);
  }
}

.horizontal-filter-container.points {
  .message {
    letter-spacing: -0.5px;
    gap: 0px;
    @media only screen and (min-width: $breakpoint-medium) {
      left: 3rem
    } 
  }
}

.reset-message { 
  font-size: .875rem;
  display: flex;
  align-items: center;
  gap: 4px;
  color: #5fa8da;

  .material-symbols-sharp {
    font-size: .875rem;    
  }
}


</style>